/**
 * Typed wrappers for the OpenPowerlifting Records API.
 *
 * The base URL comes from the build-time env var `VITE_API_BASE_URL`. In dev
 * this is read from `.env` at the project root; in a production build the
 * value is inlined into the bundle by Vite.
 *
 * All endpoints are GET-only and return JSON. There's no auth — when this
 * goes public via CloudFront you'd add an `x-api-key` header here and
 * enforce it at the edge.
 */

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

if (!BASE) {
  // Surface this loudly in dev — silent failure here would have you debugging
  // CORS errors on requests to "https://undefined/...".
  console.warn('VITE_API_BASE_URL is not set — API calls will fail.');
}

// ---- Cohort filters used by /trends/* and /rankings ----------------------

export interface CohortFilters {
  event?: string;
  equipment?: string;
  sex?: string;
  weightClass?: string;
  ageClass?: string;
  country?: string;
  state?: string;
  division?: string;
  tested?: 'true' | 'false' | '';
  federation?: string;
  parentFederation?: string;
  year?: string;
  from?: string;
  to?: string;
  // Career-position filters powered by the denormalized columns on entries.
  meetNumber?: string;          // exact (e.g. "1" = debut only)
  maxMeetNumber?: string;       // upper bound (e.g. "5" = first 5 meets)
  minMeetsInCareer?: string;    // "veterans only" (lifters with N+ total meets)
  debutOnly?: 'true' | '';      // shorthand for meetNumber=1
  // Debut-age filters — restrict the cohort to lifters who started competing
  // at a particular age (powered by JOIN to lifter_career).
  debutAge?: string;
  minDebutAge?: string;
  maxDebutAge?: string;
  activeOnly?: 'true' | '';     // last-meet within lifter_career.is_active window
  minYearsActive?: string;
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

async function get<T>(path: string, params: Record<string, any> = {}): Promise<T> {
  const url = `${BASE}${path}${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} on ${path}: ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

// ---- Response types ------------------------------------------------------

export interface TrendByYearRow {
  year: number;
  group_key?: string | null;
  sample_size: number;
  /** Distinct-lifter count for the cell. NULL when served from the
   *  pre-agg path, which can't sum distincts across cells without
   *  inflating the count. The pre-agg path is only used when groupBy
   *  is empty, so charts that need accurate per-(year × group) counts
   *  always get them via the live aggregation fallback. */
  lifter_count: number | null;
  meet_count: number | null;
  avg_dots: number | null;
  avg_total_kg: number | null;
  avg_squat_kg: number | null;
  avg_bench_kg: number | null;
  avg_deadlift_kg: number | null;
  median_dots: number | null;
  median_total_kg: number | null;
  /** Per-lift medians — populated only on the live-aggregation path
   *  (the pre-agg table only stores median Dots, not per-lift). The
   *  chart switches to live when the user picks a median metric. */
  median_squat_kg: number | null;
  median_bench_kg: number | null;
  median_deadlift_kg: number | null;
  p25_dots: number | null;
  p75_dots: number | null;
}

export interface TrendByMeetNumberRow extends Omit<TrendByYearRow, 'year'> {
  meet_number: number;
  avg_months_since_debut: number | null;
  // group_key, p25_dots, p75_dots inherited from TrendByYearRow
}

// One bar of the "median Dots at each career meet" chart. meet_bucket is
// 1..9 for a specific full-power meet, or 10 for the "10+" cohort. When
// is_peak is true (only the 10+ bucket) median_dots is the median PEAK Dots
// of 10+-meet lifters rather than their Dots at a single meet.
export interface MedianDotsByMeetRow {
  meet_bucket: number;
  median_dots: number;
  lifter_count: number;
  is_peak: boolean;
}

export interface DebutAgeRow {
  debut_age: number;
  cohort_size: number;
  // Both mean and median exposed for the skew-prone career-length
  // columns. The UI defaults to median because total_meets and
  // years_active are heavily right-skewed (long tail of veterans
  // pulling the mean way above the typical lifter).
  avg_years_active: number | null;
  median_years_active: number | null;
  avg_total_meets: number | null;
  median_total_meets: number | null;
  avg_peak_dots: number | null;
  median_peak_dots: number | null;
  avg_peak_total_kg: number | null;
  avg_meets_per_year: number | null;
  still_active: number;
  pct_still_active: number;
}

export interface TestedSwitchRow {
  cohort: 'tested→untested' | 'untested→tested' | 'lifelong tested' | 'lifelong untested';
  sex: 'M' | 'F';
  /** Lower edge of the 50-Dot band on which this row's pre-anchor sits. */
  band: number;
  /** When the request was for `view=band-weight` or `view=band-age`,
   *  this holds the weight class or age bucket. NULL otherwise. */
  split_key: string | null;
  /** Number of (lifter, anchor) pairs in this bucket. */
  n: number;
  /** Mean Dots gained from anchor across the horizon window. */
  avg_delta: number;
  /** Median Dots gained — robust against outliers. */
  median_delta: number;
}

export interface PedEffectSummary {
  /** Mean Dots/horizon advantage of "any untested experience" cohort
   *  (lifetime untested + clean tested→untested switchers) over the
   *  matched lifelong-tested control. */
  effect: number;
  /** 95% lower bound (effect ± 1.96·SE). */
  ci_low: number;
  /** 95% upper bound. */
  ci_high: number;
  /** Standard error of the effect estimate. */
  se: number;
  /** Sample-weighted mean Dots gain in the untested-experienced cohort. */
  untested_avg: number;
  /** Sample-weighted mean Dots gain in the lifelong-tested control. */
  tested_avg: number;
  n_untested: number;
  n_tested: number;
  n_cells: number;
}

export interface PedEffectGroupRow extends Partial<PedEffectSummary> {
  /** Bucket key — age bucket / weight bracket / Dots band / sex code,
   *  depending on which breakdown this row came from. */
  key: string;
}

export interface AttemptStrategyRow {
  lift: 'squat' | 'bench' | 'deadlift';
  n_total: number;
  n_2nd_pr: number;
  n_3rd_only: number;
  /** % of meets in cohort where the 2nd attempt was a clean career PR
   *  (above prev career best AND no recent attempted-higher pseudo-PRs
   *  in the prior 12 months). */
  pct_2nd_pr: number | null;
  /** Conditional success rate of the 3rd attempt within the 2nd-PR group. */
  pct_2nd_pr_hits_3rd: number | null;
  /** Avg kg added to career best at meets in each PR group. Stays in
   *  kg for unit-aware formatting on the client. */
  avg_gain_2nd_pr_kg: number | null;
  avg_gain_3rd_only_kg: number | null;
  /** Aggressive-2nd-intent subgroup — meets where the lifter took the
   *  2nd at PR weight (made or missed). Make rate is the success rate
   *  of "going for it on attempt 2". */
  n_aggressive_2nd_attempts: number;
  pct_aggressive_2nd_made: number | null;
  /** Conservative-play subgroup (made the 2nd at a non-PR weight).
   *  Two rates from the same denominator:
   *    · made-3rd rate (parallel to pct_2nd_pr_hits_3rd, displayed in
   *      the per-lift stat tile)
   *    · set-PR-via-3rd rate (drives the headline "X× more likely"
   *      callout: ratio = 100% / pct_conservative_2nd_set_pr) */
  n_conservative_2nd: number;
  pct_conservative_2nd_made_3rd: number | null;
  pct_conservative_2nd_set_pr: number | null;
}
export interface AttemptStrategyPayload {
  cohort: { sex: string; equipment: string; event: string };
  filters: { max_days_between: number };
  rows: AttemptStrategyRow[];
}

export interface PedEffectPayload {
  horizon_months: 12 | 24;
  n_cells_total: number;
  /** Stratified-matched estimator. Headline number we display first —
   *  controls for sex/age/weight/starting-Dots/experience composition
   *  differences between cohorts. */
  headline: PedEffectSummary | null;
  /** Raw unconditional cohort difference. Useful as a footnote — the
   *  untested cohort skews older and stronger so the raw gap is
   *  smaller than the matched gap. */
  raw_headline: PedEffectSummary | null;
  by_sex: PedEffectGroupRow[];
  by_age: PedEffectGroupRow[];
  by_weight: PedEffectGroupRow[];
  by_band: PedEffectGroupRow[];
  by_experience: PedEffectGroupRow[];
}

export interface TestedSwitchPayload {
  horizon_months: 12 | 24;
  view: 'band' | 'band-weight' | 'band-age';
  min_n: number;
  cohort_sizes: { cohort: string; lifters: number }[];
  rows: TestedSwitchRow[];
}

/** /analysis/time-to-peak — years from debut to peak-Dots meet, lifelong
 *  tested vs untested, as a cumulative curve ("share peaked by year N"). */
export interface TimeToPeakPayload {
  sex: string | null;
  year_cap: number;
  cohorts: {
    cohort: 'tested' | 'untested';
    n: number;
    median_years: number | null;
    points: { year: number; cum_pct: number | null }[];
  }[];
}

/** /analysis/elite-factors — what early-career variables separate the
 *  top-`elite_pct`% of lifters (by peak Dots, cut PER sex × equipment)
 *  from the field. */
export interface EliteThreshold {
  sex: string;
  equipment: string;
  /** Peak-Dots cutoff to count as elite for this sex × equipment. */
  threshold_dots: number;
  n_total: number;
  n_elite: number;
  elite_rate_pct: number | null;
}
export interface EliteComparisonRow {
  key: string;
  label: string;
  unit: string;
  /** True when the values are kg — frontend converts for display. */
  kg: boolean;
  elite_avg: number | null;
  field_avg: number | null;
  gap: number | null;
}
export interface EliteBin {
  lo: number;
  hi: number;
  n: number;
  n_elite: number;
  elite_rate_pct: number;
}
export interface ElitePredictor {
  key: string;
  label: string;
  unit: string;
  kg: boolean;
  bins: EliteBin[];
}
export interface EliteFactorsPayload {
  cohort: { event: string; equipment: string; sex: string };
  elite_pct: number;
  min_total_meets: number;
  sample_size: number;
  overall_elite_rate_pct: number;
  thresholds: EliteThreshold[];
  comparison: EliteComparisonRow[];
  predictors: ElitePredictor[];
}

/** /analysis/lift-ratios — squat/bench/deadlift share of Total, averaged
 *  per (sex × weight class × tested) for full-power lifters. */
export interface LiftRatioRow {
  sex: 'M' | 'F';
  weight_class_kg: string;
  n: number;
  squat_pct: number;
  bench_pct: number;
  deadlift_pct: number;
  avg_bodyweight_kg: number | null;
}
export interface LiftRatiosPayload {
  cohort: { event: string; equipment: string };
  min_n: number;
  rows: LiftRatioRow[];
}

/** /analysis/cohort-outcomes — "lifters who were where you are". For the
 *  viewer's bucket (sex, equipment, career-meet, Dots band), the share of
 *  that anonymous cohort who later reached each higher milestone within
 *  1 / 3 / 5 years. `matched` is false when the bucket is too thin. */
export interface CohortOutcomesPayload {
  matched: boolean;
  sex: string;
  equipment: string;
  meet_bucket: number;
  dots_band: number;
  current_dots: number;
  cohort_n: number;
  milestones: { milestone: number; pct_1y: number; pct_3y: number; pct_5y: number; n_1y: number; n_3y: number; n_5y: number }[];
}

/** /analysis/age-distribution — share of competition results per ~3-year
 *  age bucket, for the whole cohort and for the elite (Dots ≥ elite_dots)
 *  subset. Each pct series is normalized within its own group. */
export interface AgeDistributionBucket {
  lo: number;
  /** Upper edge (exclusive). Null for the open top bucket ("51+"). */
  hi: number | null;
  label: string;
  n_all: number;
  n_elite: number;
  pct_all: number;
  pct_elite: number;
}
export interface AgeDistributionPayload {
  cohort: { event: string; equipment: string | null; sex: string | null; weight_class_kg: string | null };
  elite_dots: number;
  total_all: number;
  total_elite: number;
  buckets: AgeDistributionBucket[];
}

/** /analysis/outlier-profile — "against-the-odds" elites: how often lifters
 *  reached eliteDots+ peak Dots despite a regression-confirmed negative trait,
 *  whether those handicaps stack, and the most improbable elites by name. */
export interface OutlierFactor {
  key: string;
  label: string;
  unit: string;
  /** Univariate slope of peak Dots on this trait (native units). */
  slope: number;
  /** Independent standardized coefficient from the multiple regression. */
  coef_std: number;
  verdict: string;
  direction: string;
  /** Which tail is unfavorable — 'high' (e.g. late debut) or 'low' (e.g. weak debut). */
  unfavorable: 'high' | 'low';
  /** True when the unfavorable tail predicts lower peak Dots both alone and independently. */
  is_negative: boolean;
  population_median: number;
  /** Quartile cutoff at which a lifter counts as having "defied" this trait. */
  cutoff: number;
  n_elite_defied: number;
  pct_elite_defied: number;
  /** Mean trait value among elites who defied it (vs population_median). */
  elite_defier_mean: number | null;
}
export interface OutlierLifterOvercame {
  key: string;
  label: string;
  unit: string;
  value: number;
  population_median: number;
}
export interface OutlierLifter {
  lifter_id: number;
  name: string;
  peak_dots: number;
  /** Full-power debut Dots / age (first SBD meet ≥250). */
  debut_dots: number;
  debut_age: number;
  /** Full-power career span (years, first→last SBD meet) — shown for a
   *  merged-lifter sanity check. */
  career_years: number;
  n_defied: number;
  /** Improbability score — Σ over defied traits of |independent coef| × severity. */
  index: number;
  overcame: OutlierLifterOvercame[];
}
/** One elite lifter as a point for the debut-vs-peak scatter. */
export interface OutlierScatterPoint {
  x: number;   // debut Dots
  y: number;   // peak Dots
  ato: boolean; // is an against-the-odds (top ranked) lifter
}
export interface OutlierProfilePayload {
  cohort: { equipment: string; sex: string | null; elite_dots: number; min_meets: number };
  cohort_size: number;
  elite_count: number;
  model: { r_squared: number; n: number } | null;
  factors: OutlierFactor[];
  /** Distribution of how many negatives each elite defied (stacking view). */
  outlier_count_hist: { defied: number; n: number; pct: number }[];
  /** Every elite lifter as (debut Dots, peak Dots) for the scatter. */
  scatter: OutlierScatterPoint[];
  /** Weak-debut threshold (bottom-quartile debut Dots) — a reference line. */
  debut_weak_cutoff: number | null;
  against_the_odds: OutlierLifter[];
}

export interface Milestone {
  id: string;
  title: string;
  story: string;
  icon: string;
  threshold: number;
  threshold_field: 'total_kg' | 'best3_squat_kg' | 'best3_bench_kg' | 'best3_deadlift_kg' | 'dots';
  lifter: { lifter_id: number; name: string };
  meet: { meet_id: number; meet_name: string | null; federation: string | null; meet_date: string };
  result: {
    total: number | null;
    squat: number | null;
    bench: number | null;
    deadlift: number | null;
    dots: number | null;
    equipment: string;
    sex: string;
    weight_class_kg: string | null;
    bodyweight_kg: number | null;
  };
}

export interface SpendSnapshotPayload {
  /** When false, no snapshot exists yet — caller should fall back to
   *  placeholder copy. When true, all the other fields below are present. */
  available: boolean;
  reason?: string;
  /** YYYY-MM string for the month being summarized. */
  month?: string;
  /** Sum of all line items in dollars. */
  total?: number;
  /** AWS-reported flag — true while the month is in progress. */
  estimated?: boolean;
  /** ISO timestamp the snapshot was generated. */
  lastRefreshed?: string;
  /** Display lines bucketed into our categories, descending by amount. */
  lines?: { category: string; amount: number }[];
  /** Raw AWS service breakdown — kept for transparency. */
  rawGroups?: { service: string; amount: number }[];
  /** Rolling month history (newest first). Present on snapshots written by
   *  the multi-month cost lambda; absent on older single-month snapshots. */
  months?: {
    month: string;
    total: number;
    estimated: boolean;
    lines: { category: string; amount: number }[];
    rawGroups?: { service: string; amount: number }[];
  }[];
}

export interface PeakMeetRow {
  meet_number: number;
  n: number;
  /** Share of the total cohort whose lifetime peak Dots came at this meet. */
  pct: number;
  /** Running cumulative share — by this meet number, this fraction
   *  had already hit their peak. */
  cumulative_pct: number;
}
export interface PeakMeetPayload {
  min_meets: number;
  max_meet: number;
  total_lifters: number;
  rows: PeakMeetRow[];
}

export interface DebutToPeakRow {
  sex: 'M' | 'F';
  debut_band: number;
  n: number;
  avg_peak: number;
  median_peak: number;
  p25_peak: number;
  p75_peak: number;
}
export interface DebutToPeakPayload {
  min_meets: number;
  min_band: number;
  max_band: number;
  min_n: number;
  rows: DebutToPeakRow[];
}

/** /trends/career-journey — everything the Career "journey" page needs in
 *  one call, for a single sex cohort (`all` | `M` | `F`). It's a curated
 *  Raw + Wraps full-power (SBD) narrative, so equipment/event are fixed
 *  server-side and echoed back for display.
 *
 *  Note: the median / pct / cum fields come back as STRINGS (Postgres
 *  numeric serialization) — coerce with Number() before doing math. The
 *  lifter / n counts are already numbers. */
export interface CareerJourneyArcRow { meet: number; median_dots: string; lifters: number; }
export interface CareerJourneyAgeRow { age: number; median_dots: string; n: number; }
export interface CareerJourneyRarityRow { threshold: number; pct: number; lifters: number; }
export interface CareerJourneyPeakRow { meet: number; n: number; pct: string; cum: string; }
export interface CareerJourneyDebutRow { debut_age: number; median_peak: string; n: number; }
export interface CareerJourneyPayload {
  sex: string;
  equipment: string;
  event: string;
  /** 10 rows (meet 1..10). Powers the climb arc + the drop-off survival curve. */
  arc: CareerJourneyArcRow[];
  age: CareerJourneyAgeRow[];
  /** Dots ceilings 300..600. */
  rarity: CareerJourneyRarityRow[];
  /** Peak-meet distribution, meet 1..15. */
  peak: CareerJourneyPeakRow[];
  debut: CareerJourneyDebutRow[];
}

/** /trends/journey — one row per year (full power, 1994+) powering the
 *  redesigned Trends page. all_lifters is the boom; women_pct the women's
 *  wave; raw_share_pct the raw revolution; rw/eq_median_dots are the median
 *  Dots of the Raw+Wraps vs Equipped (single/multi/unlimited) cohorts. rw_n /
 *  eq_n are per-year sample sizes so a line can be gated off where it's thin. */
export interface TrendsJourneyRow {
  year: number;
  all_lifters: number;
  /** Distinct lifters that year across ANY event (superset of all_lifters).
   *  Null on the cohort-filtered path (all-events doesn't apply to a slice). */
  all_event_lifters: number | null;
  women_lifters: number;
  women_pct: number | null;
  rw_n: number;
  eq_n: number;
  raw_share_pct: number | null;
  rw_median_dots: number | null;
  eq_median_dots: number | null;
}
export interface TrendsJourneyPayload {
  event: string;
  /** true when a cohort filter was applied (live query) vs the precomputed MV. */
  filtered?: boolean;
  rows: TrendsJourneyRow[];
}

/** /trends/federation-participation — unique lifters per (child) federation
 *  per year, for the top-N federations by all-time distinct lifters.
 *  `federations` is the rank-ordered legend list; `rows` is the flat series. */
export interface FederationParticipationRow {
  federation: string;
  year: number;
  unique_lifters: number;
}
export interface FederationParticipationPayload {
  n: number;
  federations: { federation: string; total_lifters: number }[];
  rows: FederationParticipationRow[];
}


/** /trends/weight-class-transitions — per-(direction, from_class,
 *  to_class) median deltas in total/dots when lifters move classes,
 *  joined against a same-class control for the same from_class so
 *  the card can show "moved 83→93 = +12kg total vs stayed in 83 =
 *  +5kg → ~+7kg attributable to the class change". */
export interface WeightClassTransitionRow {
  direction: 'up' | 'down';
  from_class: string;
  to_class: string;
  n: number;
  median_delta_total: number | null;
  median_delta_dots: number | null;
  p25_delta_total: number | null;
  p75_delta_total: number | null;
  median_days_between: number;
  control_n: number | null;
  control_median_delta_total: number | null;
  control_median_delta_dots: number | null;
  control_median_days_between: number | null;
}
export interface WeightClassTransitionsPayload {
  cohort: { sex: string; equipment: string; event: string };
  filters: {
    min_days_between: number;
    edge_threshold_kg: number;
    min_n: number;
  };
  rows: WeightClassTransitionRow[];
}

/** One lifter's transition between two consecutive SBD meets, used by
 *  the scatter card on Career arc. `from_*` is the lifter's last meet
 *  in the old bucket; `to_*` is their first meet in the new bucket. */
export interface WcTransitionDetailRow {
  lifter_id: number;
  lifter_name: string;
  from_dots: number;
  to_dots: number;
  from_total_kg: number;
  to_total_kg: number;
  from_bw_kg: number;
  to_bw_kg: number;
  days_between: number;
  from_date: string;
  to_date: string;
}
export interface WcTransitionDetailSummary {
  n: number;
  median_delta_dots: number;
  median_delta_total: number;
  median_days_between: number;
  p25_delta_dots: number;
  p75_delta_dots: number;
}
export interface WcTransitionDetailPayload {
  cohort: { sex: string; equipment: string; event: string; from_bucket: string; to_bucket: string };
  filters: { min_days_between: number; max_days_between: number };
  rows: WcTransitionDetailRow[];
  summary: WcTransitionDetailSummary | null;
  control: WcTransitionDetailSummary | null;
}

export interface DotsThresholdsPayload {
  total_lifters: number;
  thresholds: { threshold: number; lifters: number; pct: number }[];
  cohort: any;
}

export interface RegressionResult {
  name: string;
  x_label: string;
  y_label: string;
  x_unit?: string;
  y_unit?: string;
  slope: number;
  intercept: number;
  r: number;
  r_squared: number;
  n: number;
  p_value: number | null;
  /** Single combined effect-size + significance verdict. Replaced the
   *  prior dual (strength, significance) display because in large samples
   *  every regression hits "very significant" by p-value alone. */
  verdict: 'strong' | 'moderate' | 'weak' | 'negligible' | 'inconclusive';
  direction: 'positive' | 'negative' | 'flat';
  interpretation: string;
}

export interface YearlyAnalysisRow {
  year: number;
  unique_lifters: number;
  avg_dots: number | null;
  avg_total_kg: number | null;
  avg_squat: number | null;
  avg_bench: number | null;
  avg_deadlift: number | null;
  elite_avg_dots: number | null;
  elite_avg_total_kg: number | null;
  elite_avg_squat: number | null;
  elite_avg_bench: number | null;
  elite_avg_deadlift: number | null;
}

export interface AgeCurveRow extends Omit<TrendByYearRow, 'year' | 'group_key'> {
  age: number;
}

export interface TopNRow {
  year: number;
  sample_size: number;
  top_n_avg: number | null;
  peak_value: number | null;
  bottom_of_top_n: number | null;
}

export interface RetentionRow {
  debut_year: number;
  cohort_size: number;
  pct_reached_meet_2: number;
  pct_reached_meet_5: number;
  pct_reached_meet_10: number;
  avg_career_meets: number | null;
}

export interface CohortPeer {
  lifter_id: number;
  name: string;
  debut_dots: number;
  peak_dots: number;
  peak_total_kg: number | null;
  years_active: number | null;
  total_meets: number;
  debut_weight_class_kg: string | null;
  first_meet_date: string | null;
  last_meet_date: string | null;
  /** True when last_meet_date is within the last 24 months. Lets the
   *  UI tag actively-competing peers vs retired/inactive ones. */
  is_active: boolean;
  /** Computed peer-relative gap: peer.peak_dots - this lifter's peak_dots. */
  dots_ahead: number;
}
export interface CohortPeersPayload {
  lifter_id: number;
  has_peers: boolean;
  peers: CohortPeer[];
  anchor?: {
    debut_dots: number;
    peak_dots: number;
    sex: 'M' | 'F';
    last_weight_class_kg: string | null;
  };
  reason?: string;
}

export interface LifterSearchRow {
  lifter_id: number;
  name: string;
  entry_count: number;
  meet_count: number;
  pr_total_kg: number | null;
  pr_dots: number | null;
  pr_wilks: number | null;
  pr_glossbrenner: number | null;
  pr_goodlift: number | null;
}

export interface LifterSearchResponse {
  count: number;
  lifters: LifterSearchRow[];
  mode: 'top' | 'search';
  equipment: string | null;
  sex: 'M' | 'F' | null;
  weight_class_kg: string | null;
  tested: boolean | null;
}

export interface ProgressionRow {
  entry_id: number;
  meet_number: number;
  meet_date: string;
  months_since_debut: number | null;
  months_since_prev: number | null;
  event: string;
  equipment: string;
  weight_class_kg: string | null;
  bodyweight_kg: number | null;
  age: number | null;
  age_class: string | null;
  division: string | null;
  sex: string | null;
  total_kg: number | null;
  dots: number | null;
  wilks: number | null;
  goodlift: number | null;
  glossbrenner: number | null;
  place: string | null;
  place_numeric: number | null;
  // Per-attempt fields (negative values = failed). 4th attempts don't count
  // toward the total but are kept in records.
  squat1_kg: number | null;
  squat2_kg: number | null;
  squat3_kg: number | null;
  squat4_kg: number | null;
  best3_squat_kg: number | null;
  bench1_kg: number | null;
  bench2_kg: number | null;
  bench3_kg: number | null;
  bench4_kg: number | null;
  best3_bench_kg: number | null;
  deadlift1_kg: number | null;
  deadlift2_kg: number | null;
  deadlift3_kg: number | null;
  deadlift4_kg: number | null;
  best3_deadlift_kg: number | null;
  failed_squat_attempts: number | null;
  failed_bench_attempts: number | null;
  failed_deadlift_attempts: number | null;
  delta_dots_vs_prev: number | null;
  delta_total_vs_prev: number | null;
  delta_squat_vs_prev: number | null;
  delta_bench_vs_prev: number | null;
  delta_deadlift_vs_prev: number | null;
  delta_dots_vs_debut: number | null;
  delta_total_vs_debut: number | null;
  meet_id: number;
  federation: string;
  parent_federation: string | null;
  meet_name: string;
  meet_country: string;
  meet_state: string;
  /** Number of other lifters in same event/equipment/sex at the meet
   *  with a lower total than this one. */
  lifters_below_at_meet: number;
  total_lifters_at_meet: number;
}

/** Returned from the lazy /lifters/{id}/percentiles endpoint. The
 *  frontend slots these into the progression table after it renders. */
export interface PercentileRow {
  entry_id: number;
  /** "Top X%" within the entry's event/equipment/weight class/division/sex
   *  slice. Lower is stronger. NULL when the entry has no Dots score. */
  percentile_dots: number | null;
  /** Cohort denominator. NULL when the denorm column hasn't been
   *  populated yet (post-deploy, pre-crawler-run). */
  cohort_size: number | null;
}

export interface EntryComparisonPayload {
  entry: {
    entry_id: number;
    event: string;
    equipment: string | null;
    weight_class_kg: string | null;
    sex: string | null;
    age: number | null;
    division: string | null;
    meet_number: number;
    total_kg: number | null;
    dots: number | null;
  };
  /** Which cohort the peer averages were computed over.
   *  `first-meet` — debut card; restricted to other lifters' first meets.
   *  `all-time`   — everything else; entire slice across all career stages.
   *  `unavailable` — entry was missing required fields. */
  basis: 'first-meet' | 'all-time' | 'unavailable';
  peer: {
    count: number;
    avg_total_kg: number | null;
    avg_dots: number | null;
    /** Rank position of THIS entry within the cohort by Dots (1 = best).
     *  Null if this entry has no Dots score (so no comparison possible). */
    rank_by_dots: number | null;
    /** Cohort size including this entry — denominator for the rank. */
    rank_total: number;
  } | null;
  age_peer: {
    age: number;
    count: number;
    avg_total_kg: number | null;
    avg_dots: number | null;
    rank_by_dots: number | null;
    rank_total: number;
  } | null;
}

export interface MeetSummary {
  meet_id: number;
  meet_name: string;
  meet_date: string;
  federation: string;
  parent_federation: string | null;
  meet_country: string | null;
  meet_state: string | null;
  meet_town: string | null;
  sanctioned: boolean | null;
  entry_count: number;
  lifter_count: number;
}

export interface MeetRosterRow {
  entry_id: number;
  lifter_id: number;
  name: string;
  sex: string | null;
  age: number | null;
  age_class: string | null;
  division: string | null;
  event: string;
  equipment: string | null;
  weight_class_kg: string | null;
  bodyweight_kg: number | null;
  best3_squat_kg: number | null;
  best3_bench_kg: number | null;
  best3_deadlift_kg: number | null;
  total_kg: number | null;
  dots: number | null;
  wilks: number | null;
  goodlift: number | null;
  glossbrenner: number | null;
  place: string | null;
  tested: boolean | null;
  country: string | null;
  state: string | null;
  /** Meet-wide standing by Dots among full-power (SBD) entries, and the
   *  size of that field. Only meaningful for SBD rows. Added for the
   *  meet-recap share card. Class placement uses the authoritative stored
   *  `place`; `class_field_n` is just the denominator it was scored
   *  against (division-wide or division+class, auto-detected server-side). */
  overall_dots_rank: number | null;
  overall_dots_n: number | null;
  class_field_n: number | null;
}

export interface MeetDetailPayload {
  meet: MeetSummary;
  roster_size: number;
  roster: MeetRosterRow[];
}

export interface MetaPayload {
  parent_federations: { parent_federation: string; meet_count: number }[];
  federations: { federation: string; meet_count: number }[];
  countries: { country: string; entry_count: number }[];
  weight_classes_by_parent: {
    IPF: { weight_class_kg: string; entry_count: number }[];
    Traditional: { weight_class_kg: string; entry_count: number }[];
    All: { weight_class_kg: string; entry_count: number }[];
  };
  /** Latest meet date in the database (YYYY-MM-DD) plus rough corpus
   *  size. Surfaces in the footer as "Data through MM/DD/YYYY" so users
   *  know how recent the platform stats they're reading are. */
  data_freshness: {
    latest_meet_date: string | null;
    total_meets: number;
    total_entries: number;
  } | null;
}

// ---- API methods ---------------------------------------------------------

export const api = {
  meta: () => get<MetaPayload>('/meta'),

  trendsByYear: (filters: CohortFilters & { groupBy?: string; minYear?: number; maxYear?: number; minSampleSize?: number; precision?: 'full'; avgMetricsOnly?: boolean }) =>
    get<{ rows: TrendByYearRow[]; group_by: string | null; cohort: any }>('/trends/by-year', filters),
  // (`maxYear` already supported by the endpoint — see lambda/api/trends.ts)

  trendsByMeetNumber: (filters: CohortFilters & { groupBy?: string; maxMeets?: number; minSampleSize?: number }) =>
    get<{ rows: TrendByMeetNumberRow[]; group_by: string | null; cohort: any }>('/trends/by-meet-number', filters),

  trendsMedianDotsByMeet: (filters: CohortFilters) =>
    get<{ rows: MedianDotsByMeetRow[]; cohort: any }>('/trends/median-dots-by-meet', filters),

  trendsByDebutAge: (filters: { sex?: string; equipment?: string; minAge?: number; maxAge?: number; minCohortSize?: number; minMeets?: number }) =>
    get<{ rows: DebutAgeRow[]; cohort: any }>('/trends/by-debut-age', filters),

  analysisYearly: (filters: CohortFilters & { topN?: number; fromYear?: number; toYear?: number; unit?: 'kg' | 'lbs' }) =>
    get<{ rows: YearlyAnalysisRow[]; regressions: RegressionResult[]; top_n: number; year_range: [number, number]; cohort: any }>('/analysis/yearly', filters),

  analysisCareerFactors: (filters: { sex?: string; equipment?: string; minTotalMeets?: number; minDebutAge?: number; maxDebutAge?: number; unit?: 'kg' | 'lbs' }) =>
    get<{
      regressions: RegressionResult[];
      sample_size: number;
      binned_by_age: { debut_age: number; n: number; avg_peak_dots: number | null; avg_peak_total_kg: number | null; avg_total_meets: number | null }[];
      cohort: any;
    }>('/analysis/career-factors', filters),

  analysisCareerLengthFactors: (filters: { sex?: string; equipment?: string; minTotalMeets?: number; minDebutAge?: number; maxDebutAge?: number; minDebutTotalKg?: number; maxDebutTotalKg?: number; unit?: 'kg' | 'lbs' }) =>
    get<{
      regressions: RegressionResult[];
      sample_size: number;
      binned_by_age: { debut_age: number; n: number; avg_total_meets: number | null; avg_years_active: number | null }[];
      cohort: any;
    }>('/analysis/career-length-factors', filters),

  trendsAgeCurve: (filters: CohortFilters & { minAge?: number; maxAge?: number; minSampleSize?: number }) =>
    get<{ rows: AgeCurveRow[]; cohort: any }>('/trends/age-curve', filters),

  trendsTopN: (filters: CohortFilters & { metric?: string; n?: number; minYear?: number; maxYear?: number }) =>
    get<{ rows: TopNRow[]; metric: string; n: number; cohort: any }>('/trends/top-n', filters),

  trendsDotsThresholds: (filters: CohortFilters) =>
    get<DotsThresholdsPayload>('/trends/dots-thresholds', filters),

  trendsPeakMeet: (opts: { minMeets?: number; maxMeet?: number } = {}) =>
    get<PeakMeetPayload>('/trends/peak-meet', opts),

  trendsDebutToPeak: (opts: { minMeets?: number; minBand?: number; maxBand?: number; minN?: number } = {}) =>
    get<DebutToPeakPayload>('/trends/debut-to-peak', opts),

  // Career "journey" page — one call returns every scene's data for a
  // cohort (sex = all | M | F). Raw + Wraps full-power is fixed server-side.
  careerJourney: (opts: { sex?: string } = {}) =>
    get<CareerJourneyPayload>('/trends/career-journey', opts),

  trendsJourney: (filters: { equipment?: string; sex?: string; ageClass?: string; weightClass?: string; tested?: string } = {}) =>
    get<TrendsJourneyPayload>('/trends/journey', filters),

  trendsFederationParticipation: (filters: { n?: number } = {}) =>
    get<FederationParticipationPayload>('/trends/federation-participation', filters),

  trendsWeightClassTransitions: (opts: { sex?: string; equipment?: string; min_days_between?: number; edge_threshold_kg?: number; min_n?: number } = {}) =>
    get<WeightClassTransitionsPayload>('/trends/weight-class-transitions', opts),

  trendsWcTransitionDetail: (opts: {
    sex: string; from_bucket: string; to_bucket: string;
    equipment?: string; min_days_between?: number; max_days_between?: number;
  }) => get<WcTransitionDetailPayload>('/trends/wc-transition-detail', opts),

  spend: () => get<SpendSnapshotPayload>('/spend'),

  // Calculators page (/calculators).
  toolsPercentile: (opts: {
    sex: 'M' | 'F'; equipment: string; weight_class: string; event?: string;
    tested?: 'true' | 'false'; age?: number; age_band?: number;
    squat?: number; bench?: number; deadlift?: number; total?: number; dots?: number;
  }) => get<PercentilePayload>('/tools/percentile', opts),

  toolsPlacement: (opts: {
    sex: 'M' | 'F'; equipment: string; weight_class: string; total: number;
    event?: string; tested?: 'true' | 'false'; min_field?: number;
  }) => get<PlacementPayload>('/tools/placement', opts),

  analysisTestedSwitch: (opts: { horizon?: 12 | 24; view?: 'band' | 'band-weight' | 'band-age'; minN?: number } = {}) =>
    get<TestedSwitchPayload>('/analysis/tested-switch', opts),

  analysisTimeToPeak: (opts: { sex?: string } = {}) =>
    get<TimeToPeakPayload>('/analysis/time-to-peak', opts),

  analysisPedEffect: (opts: { horizon?: 12 | 24 } = {}) =>
    get<PedEffectPayload>('/analysis/ped-effect', opts),

  analysisAttemptStrategy: (opts: { sex?: string; max_days_between?: number } = {}) =>
    get<AttemptStrategyPayload>('/analysis/attempt-strategy', opts),

  analysisEliteFactors: (opts: { sex?: string; equipment?: string; elitePct?: 1 | 2 | 5; minTotalMeets?: number } = {}) =>
    get<EliteFactorsPayload>('/analysis/elite-factors', opts),

  analysisLiftRatios: (opts: { equipment?: string; minN?: number } = {}) =>
    get<LiftRatiosPayload>('/analysis/lift-ratios', opts),

  analysisCohortOutcomes: (opts: { sex: string; equipment: string; meetNo: number; dots: number; age?: number }) =>
    get<CohortOutcomesPayload>('/analysis/cohort-outcomes', opts),

  analysisAgeDistribution: (opts: { sex?: string; equipment?: string; event?: string; weightClass?: string; eliteDots?: number } = {}) =>
    get<AgeDistributionPayload>('/analysis/age-distribution', opts),

  analysisOutlierProfile: (opts: { sex?: string; equipment?: string; eliteDots?: number; minMeets?: number } = {}) =>
    get<OutlierProfilePayload>('/analysis/outlier-profile', opts),

  analysisBodyweightScaling: (filters: CohortFilters) =>
    get<{ rows: { bw: number; avg_total: number | null; avg_dots: number | null; n: number }[]; cohort: any }>('/analysis/bodyweight-scaling', filters),
  analysisSexGap: (filters: CohortFilters) =>
    get<{ byYear: { year: number; m_dots: number | null; f_dots: number | null }[]; byLift: { lift: string; m: number | null; f: number | null }[]; cohort: any }>('/analysis/sex-gap', filters),
  analysisAttempts: (filters: CohortFilters) =>
    get<{ rows: { bucket: string; ord: number; n: number; bomb_pct: number; avg_made: number; clean_pct: number }[]; cohort: any }>('/analysis/attempts', filters),
  analysisBombLift: (filters: CohortFilters) =>
    get<{ squat: number; bench: number; deadlift: number; classified: number; total_bombs: number; cohort: any }>('/analysis/bomb-lift', filters),
  analysisSubtotalLead: (filters: CohortFilters) =>
    get<{ rows: { lead_min: number; n: number; win_pct: number }[]; total_divisions: number; overall_win_pct: number | null; cohort: any }>('/analysis/subtotal-lead', filters),

  trendsRetention: (filters: { sex?: string; equipment?: string; minYear?: number; maxYear?: number; minCohortSize?: number }) =>
    get<{ rows: RetentionRow[]; min_cohort_size: number; cohort: any }>('/trends/retention', filters),

  listMeets: (opts: {
    federation?: string;
    parentFederation?: string;
    year?: number | string;
    country?: string;
    name?: string;
    limit?: number;
    fromDate?: string;
    toDate?: string;
  } = {}) =>
    get<{ count: number; meets: MeetSummary[] }>('/meets', opts),

  getMeet: (id: number | string) =>
    get<MeetDetailPayload>(`/meets/${id}`),

  searchLifters: (name: string, opts: { limit?: number; equipment?: string; sex?: 'M' | 'F'; weight_class_kg?: string; tested?: boolean; points?: string } = {}) =>
    get<LifterSearchResponse>('/lifters', {
      name,
      limit: opts.limit ?? 25,
      equipment: opts.equipment,
      sex: opts.sex,
      weight_class_kg: opts.weight_class_kg,
      tested: opts.tested == null ? undefined : String(opts.tested),
      points: opts.points,
    }),

  topLifters: (opts: { limit?: number; equipment?: string; sex?: 'M' | 'F'; weight_class_kg?: string; tested?: boolean; points?: string } = {}) =>
    get<LifterSearchResponse>('/lifters', {
      limit: opts.limit ?? 25,
      equipment: opts.equipment ?? 'Raw',
      sex: opts.sex,
      weight_class_kg: opts.weight_class_kg,
      tested: opts.tested == null ? undefined : String(opts.tested),
      points: opts.points,
    }),

  lifterProgression: (id: number, opts: { event?: string; equipment?: string } = {}) =>
    get<{ lifter_id: number; name: string | null; count: number; progression: ProgressionRow[] }>(`/lifters/${id}/progression`, opts),

  lifterPeers: (id: number) =>
    get<CohortPeersPayload>(`/lifters/${id}/peers`),

  lifterEntryComparison: (id: number, entryId: number) =>
    get<EntryComparisonPayload>(`/lifters/${id}/comparison`, { entryId }),

  lifterPercentiles: (id: number) =>
    get<{ lifter_id: number; count: number; percentiles: PercentileRow[] }>(`/lifters/${id}/percentiles`),

  lifterForecast: (id: number) =>
    get<LifterForecastPayload>(`/lifters/${id}/forecast`),

  lifterRivals: (id: number, opts: { limit?: number; minMeetings?: number; maxGapDots?: number } = {}) =>
    get<LifterRivalsPayload>(`/lifters/${id}/rivals`, {
      limit: opts.limit,
      min_meetings: opts.minMeetings,
      max_gap_dots: opts.maxGapDots,
    }),

  // Retrospective: every snapshot we can already grade (horizon elapsed +
  // lifter competed in the ±90-day target window).
  predictionsRealized: (opts: { modelVersion?: string; limit?: number } = {}) =>
    get<PredictionsRealizedPayload>('/predictions/realized', {
      model_version: opts.modelVersion,
      limit: opts.limit,
    }),

  predictionsAccuracy: (opts: { modelVersion?: string; tier?: string; horizon?: number } = {}) =>
    get<PredictionsAccuracyPayload>('/predictions/accuracy', {
      model_version: opts.modelVersion,
      tier: opts.tier,
      horizon: opts.horizon,
    }),
};

export interface RealizedPrediction {
  lifter_id: number;
  name: string;
  anchor_date: string;
  target_date: string;
  horizon_mo: number;
  tier: string;
  equipment: string | null;
  weight_class_kg: string | null;
  model_version: string;
  anchor_total_kg: number;
  p25_kg: number;
  p50_kg: number;
  p75_kg: number;
  realized_meet_date: string;
  realized_total_kg: number;
  error_kg: number;
  in_band: boolean;
}

export interface PredictionsRealizedPayload {
  count: number;
  predictions: RealizedPrediction[];
}

export interface PredictionsAccuracyPayload {
  n: number;
  bias_kg: number | null;
  mae_kg: number | null;
  in_band_pct: number | null;
  cohorts: {
    model_version: string;
    tier: string;
    horizon_mo: number;
    n: number;
    bias_kg: number;
    mae_kg: number;
    in_band_pct: number;
  }[];
  filters: Record<string, unknown>;
}

export interface LiftPercentile {
  /** Cohort lifters with a value for this lift. */
  n: number | null;
  /** Percentile of the input value ("stronger than X%"). Null if no input. */
  percentile: number | null;
  /** Cohort quantile curve for context. */
  quantiles: { p10: number | null; p25: number | null; p50: number | null; p75: number | null; p90: number | null; p95: number | null };
}
export interface PercentilePayload {
  cohort: { sex: string; equipment: string; event: string; weight_class: string; age: number | null; age_band: number };
  lifter_count: number | null;
  squat: LiftPercentile;
  bench: LiftPercentile;
  deadlift: LiftPercentile;
  total: LiftPercentile;
  dots: LiftPercentile;
  inputs: Record<string, number | null>;
}

export interface PlacementPayload {
  cohort: { sex: string; equipment: string; event: string; weight_class: string };
  total: number;
  /** Meets with a real field (>= min_field) used for the odds/finish. */
  meets_counted: number | null;
  meets_all: number | null;
  min_field: number;
  win_pct: number | null;
  podium_pct: number | null;
  est_place: number | null;
  median_field: number | null;
}

export interface LifterRivalMeeting {
  meet_id: number;
  meet_date: string;
  meet_name: string | null;
  federation: string | null;
  equipment: string;
  weight_class_kg: string;
  division: string;
  lifter: {
    total: number | null;
    dots: number | null;
    place: number | null;
    squat: number | null;
    bench: number | null;
    deadlift: number | null;
  };
  rival: {
    total: number | null;
    dots: number | null;
    place: number | null;
    squat: number | null;
    bench: number | null;
    deadlift: number | null;
  };
}

export interface LifterRival {
  rival_id: number;
  rival_name: string;
  meet_count: number;
  encounter_count: number;
  wins: number;
  losses: number;
  ties: number;
  avg_dots_gap: number | null;
  first_meeting: string;
  last_meeting: string;
  cohorts: string[];
  meetings: LifterRivalMeeting[];
}

export interface LifterCloseMatchup {
  /** 'cohort' = same flight + same federation division. 'best_lifter'
   *  = cross-class drama for the best-lifter award (top-3 in their
   *  sex+equipment slice with an adjacent-rank rival within 10 Dots). */
  kind: 'cohort' | 'best_lifter';
  rival_id: number;
  rival_name: string;
  meet_id: number;
  meet_date: string;
  meet_name: string | null;
  federation: string | null;
  equipment: string;
  /** Populated for cohort cards only — the shared cohort label. */
  weight_class_kg: string | null;
  division: string | null;
  /** Populated for best_lifter cards (and also for cohort, where both
   *  are the same value) — lets the UI show "74kg vs 90kg" cross-class
   *  context on best-lifter rows. */
  my_weight_class_kg: string | null;
  rival_weight_class_kg: string | null;
  /** Best-lifter ranks within (meet × sex × equipment). Null on cohort. */
  my_rank: number | null;
  rival_rank: number | null;
  dots_gap: number;
  lifter: {
    total: number | null; dots: number | null; place: number | null;
    squat: number | null; bench: number | null; deadlift: number | null;
  };
  rival: {
    total: number | null; dots: number | null; place: number | null;
    squat: number | null; bench: number | null; deadlift: number | null;
  };
}

export interface LifterRivalsPayload {
  lifter_id: number;
  lifter_name: string;
  count: number;
  rivals: LifterRival[];
  close_matchups: LifterCloseMatchup[];
}

/** Reference-curve point: per-meet single value, used for the dashed
 *  "predicted" line on the career chart. */
export interface LifterReferencePoint {
  entry_meet_date: string;
  weeks_since_debut: number;
  total: number | null;
  squat: number | null;
  bench: number | null;
  deadlift: number | null;
}

/** Quartile band for forward-projection tiles. Tier metadata flows
 *  through from the lambda so the UI can label the band ("typical
 *  outcome for a strong-tier lifter") and surface override status
 *  (raw Dots said strong, growth rate promoted to casual). */
export interface ForecastBand {
  p25: number;
  p50: number;
  p75: number;
  tier?: 'casual' | 'average' | 'strong' | 'elite';
  base_tier?: 'casual' | 'average' | 'strong' | 'elite' | null;
  override_applied?: boolean;
  n?: number;
}

/** One forward-projection tile (+1y / +2y / +5y). */
export interface LifterForecastForwardRow {
  months_ahead: number;
  target_date: string;
  /** Anchor used for the projection — recent peak Total over last ~2y. */
  anchor_kg: number | null;
  total: ForecastBand | null;
  squat: ForecastBand | null;
  bench: ForecastBand | null;
  deadlift: ForecastBand | null;
}

export interface LifterForecastPayload {
  lifter_id: number;
  has_prediction: boolean;
  sex?: string;
  debut_date?: string;
  debut?: { total: number | null; squat: number | null; bench: number | null; deadlift: number | null };
  /** Slice the forecast was computed within — the lifter's most recent
   *  weight class + equipment. Used to label the forecast strip
   *  ("Predicted trajectory · 90kg · Raw"). */
  slice?: { weight_class_kg: string | null; equipment: string | null };
  /** One row per actual meet date — used to overlay a "predicted" reference
   *  line on the career chart alongside the lifter's actual results. */
  per_meet: LifterReferencePoint[];
  /** Forward quartile bands at +12, +24, +60 months past the most recent meet. */
  forward: LifterForecastForwardRow[];
}
